import moment from "moment-timezone"
import { DATE_FORMAT } from "src/utils/constants"

const serverMoment = (date: string) => {
   return moment(date, DATE_FORMAT.SERVER_DATE)
}

const generateDateRange = (
   startDate: string,
   startTime: string | null,
   endDate: string,
   endTime: string | null
 ) => {
   if (!startDate || !endDate) return '';
 
   const effectiveStartTime = startTime && startTime !== '' ? startTime : '00:00';
   const effectiveEndTime = endTime && endTime !== '' ? endTime : '23:59';
 
   const start = moment(`${startDate} ${effectiveStartTime}`, 'YYYY-MM-DD HH:mm');
   const end = moment(`${endDate} ${effectiveEndTime}`, 'YYYY-MM-DD HH:mm');
 
   if (!start.isValid() || !end.isValid()) return '';
 
   const isSameDay = start.isSame(end, 'day');
   const hasNoTimes = !startTime && !endTime;
   
   if (hasNoTimes) {
     if (isSameDay) {
       return `${start.format('MMM D, YYYY')} 12:00 am - 11:59 pm`;
     }
     return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
   }
 
   if (isSameDay) {
     return `${start.format('MMM D, YYYY h:mm a')} - ${end.format('h:mm a')}`;
   } else {
     return `${start.format('MMM D, YYYY h:mm a')} - ${end.format('MMM D, YYYY h:mm a')}`;
   }
 };

 const formatDateTimeCustom = (date: string | null | undefined, time: string | null | undefined) => {
  if (!date) return '';

  const dateInputFormat = date.includes(',') ? 'MMM D, YYYY' : 'YYYY-MM-DD';

  if (!time || time === '') {
    const dateMoment = moment(date, dateInputFormat);
    return dateMoment.isValid() ? dateMoment.format('DD-MM-YYYY') : '';
  }

  const combined = moment(`${date} ${time}`, [
    `${dateInputFormat} HH:mm:ss`,
    `${dateInputFormat} HH:mm`
  ]);

  if (!combined.isValid()) return '';

  return combined.format('DD-MM-YYYY h:mma');
};

// For YYYY-MM-DD date-only strings — NO timezone conversion
const formatDateOnly = (
  dateStr: string | null | undefined,
  format = 'MMM DD, YYYY'
): string => {
  if (!dateStr) return '';
  const m = moment(dateStr, 'YYYY-MM-DD', true); // strict parsing
  return m.isValid() ? m.format(format) : '';
};

// For ISO UTC datetime strings — parse UTC, display in local timezone
const formatUtcDateTime = (
  utcIsoStr: string | null | undefined,
  format = 'MMM D, YYYY, h:mm A'
): string => {
  if (!utcIsoStr) return '';
  const m = moment.utc(utcIsoStr).local();
  return m.isValid() ? m.format(format) : '';
};

// Returns a moment in local timezone, for callers that need further operations
const parseUtcToLocal = (utcIsoStr: string): moment.Moment => {
  return moment.utc(utcIsoStr).local();
};

// Normalizes any date value to YYYY-MM-DD for API submission
const formatForApi = (
  date: string | Date | moment.Moment,
  format = 'YYYY-MM-DD'
): string => {
  return moment.utc(date).format(format);
};

// Whether two YYYY-MM-DD dates fall on the same calendar day
const isSameDayEvent = (startDate: string | null | undefined, endDate: string | null | undefined): boolean => {
  if (!startDate || !endDate) return false;
  return moment(startDate).isSame(moment(endDate), 'day');
};

export { serverMoment, generateDateRange, formatDateTimeCustom, formatDateOnly, formatUtcDateTime, parseUtcToLocal, formatForApi, isSameDayEvent }